import React, { useEffect, createContext, useState, useMemo } from 'react';
import PropTypes from 'prop-types';

import { gtagEvent } from 'src/utils/gtag';
import useRestApiClient from 'src/hooks/useRestApiClient';
import { filterListBy } from 'src/utils/helpers/filter';

const initialState = {
  filteredMobileApps: [],
  isLoading: false,
  isPreloaded: false,
  filterValues: { industry: '', search_title: '', sortby: '' },
  setFilterValues: () => Promise.resolve()
};

const MobileAppsContext = createContext(initialState);

export const MobileAppsProvider = (props) => {
  const { children, ...contextProviderProps } = props;
  const [filterValues, setFilterValues] = useState(initialState.filterValues);
  const [isLoading, setLoading] = useState(false);
  const [isPreloaded, setPreloaded] = useState(false);
  const [mobileApps, setMobileApps] = useState([]);
  const [filteredMobileApps, setFilteredMobileApps] = useState([]);
  const client = useRestApiClient();

  const removeFromList = (mobileAppId) => {
    setMobileApps((prev) => {
      const filtered = prev.filter((row) => row.id !== mobileAppId);
      return [...filtered];
    });
  };

  const loadMobileApps = async (force = false) => {
    if (mobileApps.length > 0 && !force) return;
    if (isLoading) return;

    try {
      setLoading(true);
      const {
        data: { message }
      } = await client.get('/apps');
      setMobileApps(message);
      setFilteredMobileApps(message);
      setPreloaded(true);
    } catch (error) {
      setMobileApps([]);
      setFilteredMobileApps([]);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const reloadMobileApps = async () => {
    setLoading(true);
    const {
      data: { message }
    } = await client.get('/apps');
    setMobileApps(message);
    setLoading(false);
  };

  useEffect(() => {
    let filterMobileApps = mobileApps;
    const { industry, sortby, search_title: searchTitle } = filterValues;

    // make sure to only returns published records
    filterMobileApps =
      filterMobileApps.length > 0 &&
      filterMobileApps.filter((record) => +record.publish === 1);

    filterMobileApps = filterListBy(filterMobileApps, 'industry', industry);

    if (sortby) {
      let sortedsortedMobileAppss = [...filterMobileApps];
      switch (sortby) {
        case 'latest':
          sortedsortedMobileAppss = filterMobileApps.sort((a, b) => {
            return new Date(b.created_at) - new Date(a.created_at);
          });
          break;
        case 'popular':
          sortedsortedMobileAppss = filterMobileApps.sort((a, b) => {
            const firstUsage = typeof a.usage !== 'undefined' ? a.usage : 0;
            const secondUsage = typeof b.usage !== 'undefined' ? b.usage : 0;
            return secondUsage - firstUsage;
          });
          break;
        default:
          // no default option
          break;
      }
      filterMobileApps = [...sortedsortedMobileAppss];
    }

    if (searchTitle && searchTitle.length >= 3) {
      gtagEvent('search', {
        search_term: searchTitle
      });

      filterMobileApps = filterMobileApps.filter(
        ({ description, title }) =>
          (description &&
            description.toLowerCase().indexOf(searchTitle.toLowerCase()) >
              -1) ||
          (title && title.toLowerCase().indexOf(searchTitle.toLowerCase()) > -1)
      );
    }

    setFilteredMobileApps(filterMobileApps);
  }, [filterValues, mobileApps]);

  const values = useMemo(
    () => ({
      isPreloaded,
      isLoading,
      filterValues,
      mobileApps,
      filteredMobileApps,
      removeFromList,
      setFilterValues,
      loadMobileApps,
      reloadMobileApps
    }),
    [isPreloaded, isLoading, filterValues, mobileApps, filteredMobileApps]
  );

  return (
    <MobileAppsContext.Provider value={values} {...contextProviderProps}>
      {children}
    </MobileAppsContext.Provider>
  );
};

MobileAppsProvider.propTypes = {
  children: PropTypes.node.isRequired
};

export default MobileAppsContext;
